/**
 * Site Wi-Fi mesh -- server-state hooks and response types (Wireless > Mesh, Site > Mesh tab).
 *
 * Backend: api/v2/mesh.py (/sites/{id}/mesh, .../plan, .../apply); design
 * docs/design/wifi-mesh.md (OP#1767). One mesh per site, built from an AP group or an explicit
 * AP list. Self-service for testers: create (a switched-off draft) -> enable -> preview (per-AP diff) ->
 * dry run -> apply. Reads are site_operator+; every write, the plan and the apply are org_admin+
 * server-side (MESH_WRITE_ROLE) -- the RoleGates in routes/Mesh.tsx must match.
 *
 * The mesh SAE key is generated and stored server-side (secret store) and is NEVER in any
 * response: the UI only ever sees `key_set`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import { qk } from "./queryKeys";
import { useSession } from "@/stores/session";

/* ---- types ------------------------------------------------------------------------------ */

export type MeshOverride = "auto" | "root" | "point" | "exclude";
export type MeshRole = "root" | "point" | "isolated" | "off" | "unknown";
export type MeshVerdict = "supported" | "unknown" | "unsupported";
export type MeshAction = "join" | "update" | "leave" | "none";

/** A refusal or a warning: {code, message, serial}. */
export interface MeshItem {
  code: string;
  message: string;
  serial: string | null;
}

/** The capability-gate verdict for one AP (services/mesh_render.capability). */
export interface MeshCapability {
  verdict: MeshVerdict;
  allowed: boolean;
  reason: string;
  firmware_family: string;
  package_check_passed: boolean;
}

export interface MeshRunDevice {
  serial: string;
  name: string;
  action: MeshAction;
  status: "pending" | "running" | "applied" | "failed" | "skipped";
  outcome: string | null;
  reason: string | null;
  started_at: string | null;
  finished_at: string | null;
  verification: string | null;
}

export interface MeshRun {
  id: string;
  status: "running" | "succeeded" | "failed" | "interrupted";
  started_at: string | null;
  finished_at: string | null;
  started_by: string | null;
  order: string[];
  error: string | null;
  devices: MeshRunDevice[];
}

/** The mesh object (models/mesh.SiteMeshNetwork via mesh_manager.mesh_view). No key, ever. */
export interface SiteMesh {
  id: number;
  site_id: number;
  name: string;
  group: string | null;
  members: string[] | null;
  band: string;
  channel: number;
  channel_width: number;
  mesh_id: string;
  encryption: string;
  key_set: boolean;
  max_hops: number;
  enabled: boolean;
  role_overrides: Record<string, MeshOverride>;
  package_checks: Record<string, { result: "pass" | "fail"; note: string; checked_by: string | null; checked_at: string | null }>;
  version: number;
  deployed: Record<string, { version: number; since: string | null }>;
  last_run: MeshRun | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface MeshNeighbor {
  serial: string | null;
  mac: string | null;
  rssi: number | null;
  metric: number | null;
  tx_rate: number | null;
}

/** One AP's observed mesh status (mesh_manager.status). */
export interface MeshApStatus {
  serial: string;
  name: string;
  model: string | null;
  firmware: string | null;
  online: boolean;
  member: boolean;
  eligible: boolean;
  blocked_reason: string | null;
  capability: MeshCapability;
  readiness: MeshReadinessStatus;
  summary: string;
  technical: string;
  check: MeshCheck | null;
  /** A capability check could change this AP's verdict (server, role-independent). */
  check_available?: boolean;
  /** check_available AND the CALLER may run it (org_admin+, computed server-side). */
  can_check: boolean;
  override: MeshOverride;
  deployed: boolean;
  deployed_version: number | null;
  current: boolean;
  role: MeshRole;
  role_note: string | null;
  role_mismatch: boolean;
  carrier: boolean | null;
  parent: string | null;
  parent_name: string | null;
  hop_count: number | null;
  hops_exceeded: boolean;
  link_rssi: number | null;
  quality: "good" | "fair" | "poor" | "unknown" | "n/a" | null;
  neighbors: MeshNeighbor[];
  last_seen: string | null;
}

export interface MeshStatus {
  aps: MeshApStatus[];
  counts: Record<string, number>;
  alerts: MeshItem[];
  error?: string;
  /** Expected vs observed (OP#1803); null when it could not be built. */
  proposed_topology?: MeshProposedTopology | null;
}

/* ---- proposed topology (services/mesh_preview, OP#1803) --------------------------------- */

export type MeshCheckSeverity = "block" | "warn" | "info";
export type MeshExpectedRole = "root" | "point" | "off";
export type MeshLinkSource = "measured" | "assumed" | "none";
export type MeshLinkQuality = "good" | "fair" | "poor" | "unknown" | "n/a";

/** One preview check: block = Apply refused; warn = review; info = for the record. */
export interface MeshTopologyCheck {
  code: string;
  severity: MeshCheckSeverity;
  message: string;
  serial: string | null;
}

/** An AP's wired uplink: switch + port from the LLDP snapshot when Meridian has it. */
export interface MeshUplink {
  switch?: string | null;
  switch_serial?: string | null;
  port?: string | null;
  local_port?: string | null;
  vlan?: number | null;
  source?: string | null;
  carrier: boolean | null;
  /** Plain-language line, e.g. "core-sw port 1/1/7 -- cable up" / "cable up, port unknown". */
  label: string;
}

export interface MeshCandidateRoot {
  serial: string;
  name: string;
  rssi: number | null;
  quality: MeshLinkQuality;
}

export interface MeshTopologyNode {
  serial: string;
  name: string;
  model: string | null;
  online: boolean;
  override: MeshOverride;
  expected_role: MeshExpectedRole;
  role_reason: string;
  uplink: MeshUplink;
  expected_parent: string | null;
  expected_parent_name: string | null;
  candidate_roots: MeshCandidateRoot[];
  expected_hop_count: number | null;
  link_rssi: number | null;
  link_quality: MeshLinkQuality | null;
  link_source: MeshLinkSource;
  observed: {
    role: MeshRole;
    mesh_up: boolean;
    parent: string | null;
    parent_name: string | null;
    hop_count: number | null;
    link_rssi: number | null;
    quality: MeshLinkQuality | null;
  };
  differs: { field: "role" | "parent"; expected: string; observed: string; message: string }[];
}

export interface MeshTopologyExcluded {
  serial: string;
  name: string;
  status: string | null;
  reason: string;
  technical: string;
}

export interface MeshPushStep {
  step: number;
  serial: string;
  name: string;
  action: MeshAction | null;
  expected_role: MeshExpectedRole;
  outage_minutes: number;
}

export interface MeshProposedTopology {
  enabled: boolean;
  max_hops: number;
  nodes: MeshTopologyNode[];
  excluded: MeshTopologyExcluded[];
  push_order: MeshPushStep[];
  checks: MeshTopologyCheck[];
  blocked: boolean;
  counts: { roots: number; points: number; excluded: number; measured: number; assumed: number; differs: number };
  summary: string;
}

/** Readiness status of one AP (services/mesh_manager._readiness). */
export type MeshReadinessStatus =
  | "ready" | "needs_check" | "check_failed" | "check_stale" | "unsupported" | "not_managed" | "excluded";

/** Per-group counts for the "Mesh readiness" summary line. */
export interface MeshReadinessCounts {
  ready: number;
  needs_verification: number;
  unsupported: number;
  other: number;
  total: number;
}

/** The newest capability ("package") check recorded for an AP (profile tip-80211s-batman-v1). */
export interface MeshCheck {
  /** Against the AP's CURRENT firmware: a record for other firmware is "stale". */
  state: "pass" | "fail" | "stale" | "none";
  result: "pass" | "fail" | null;
  method: "auto" | "manual" | null;
  profile: string | null;
  firmware: string | null;
  checks: Record<string, boolean>;
  note: string;
  /** Redacted command output (auto) or the operator's note (manual). */
  evidence: string;
  checked_by: string | null;
  checked_at: string | null;
}

export interface MeshOptionAp {
  serial: string;
  name: string;
  model: string | null;
  firmware: string | null;
  online: boolean;
  groups: string[];
  verdict: MeshVerdict;
  eligible: boolean;
  reason: string;
  /** Two-audience readiness: `summary` is the short plain-language line, `technical` the
   *  engineering detail shown under "Technical details". */
  status: MeshReadinessStatus;
  summary: string;
  technical: string;
  firmware_family: string;
  check: MeshCheck | null;
  /** True where running the capability check can change the verdict ("unknown" APs),
   *  whoever is looking. Optional: older controllers only sent can_check. */
  check_available?: boolean;
  /** check_available AND the caller holds MESH_WRITE_ROLE (org_admin+). Computed server-side
   *  per caller -- a site operator gets false, so no button that can only 403 is offered. */
  can_check: boolean;
}

export interface MeshCheckProfile {
  name: string;
  description: string;
  criteria: { key: string; label: string }[];
  commands: string[];
}

/** One AP a create leaves out (POST /mesh 201 `left_out`, 422 exclusions_need_ack refusals). */
export interface MeshLeftOut {
  serial: string;
  name: string;
  model: string | null;
  status: MeshReadinessStatus;
  reason: string;
  technical: string;
}

/** POST /sites/{id}/mesh/package-check/{serial}[/record] -> the record + the AP's new row. */
export interface MeshCheckResult {
  record: MeshCheck & { audit_id?: number };
  ap: Pick<MeshOptionAp, "serial" | "name" | "eligible" | "status" | "summary" | "technical" | "check" | "check_available" | "can_check">;
}

export interface MeshOptions {
  defaults: { band: string; channel: number; channel_width: number; max_hops: number };
  bands: string[];
  channels: number[];
  channel_widths: number[];
  max_hops: number[];
  country_channels: Record<string, number[]>;
  groups: { tag: string; serials: string[]; eligible: number; readiness: MeshReadinessCounts }[];
  aps: MeshOptionAp[];
  check_profile: MeshCheckProfile;
}

export interface MeshPayload {
  mesh: SiteMesh | null;
  options: MeshOptions;
  status: MeshStatus | null;
  outage_warning: string;
}

/** One path-level change in an AP's preview (secret leaves arrive masked, flagged `masked`). */
export interface MeshDiffChange {
  op: "add" | "remove" | "change";
  path: string;
  before: unknown;
  after: unknown;
  masked: boolean;
}

export interface MeshPlanAp {
  serial: string;
  name: string;
  model: string | null;
  firmware: string | null;
  online: boolean;
  member: boolean;
  eligible: boolean;
  blocked_reason: string | null;
  capability: MeshCapability;
  expected_role: MeshOverride;
  observed_role: MeshRole | null;
  carrier: boolean | null;
  hop_count: number | null;
  action: MeshAction;
  changed: boolean;
  diff: MeshDiffChange[];
  refusals: MeshItem[];
  warnings: MeshItem[];
  order: number | null;
}

export interface MeshPlan {
  site_id: number;
  mesh: SiteMesh;
  plan_token: string;
  can_apply: boolean;
  dry_run_default: boolean;
  order: string[];
  aps: MeshPlanAp[];
  refusals: MeshItem[];
  warnings: MeshItem[];
  outage_warning: string;
  estimated_minutes: number;
  /** The proposed topology + severity-graded checks (OP#1803). Its block checks are refusals. */
  proposed_topology?: MeshProposedTopology | null;
}

export interface MeshApplyResult {
  dry_run: boolean;
  would_push?: string[];
  plan?: MeshPlan;
  run?: MeshRun;
  outage_warning: string;
}

/** Body of POST /sites/{id}/mesh. */
export interface MeshCreateInput {
  name: string;
  group?: string;
  members?: string[];
  band?: string;
  channel?: number;
  channel_width?: number;
  max_hops?: number;
  role_overrides?: Record<string, MeshOverride>;
  /** Serials of the APs the operator acknowledged will be LEFT OUT (stay wired-only). The
   *  server refuses the create (422 exclusions_need_ack) unless every non-ready AP is listed. */
  acknowledge_exclusions?: string[];
}

/** Body of PATCH /sites/{id}/mesh (all optional). */
export interface MeshUpdateInput extends Partial<MeshCreateInput> {
  enabled?: boolean;
  rotate_key?: boolean;
  package_check?: { serial: string; result: "pass" | "fail"; note?: string };
}

/* ---- hooks ------------------------------------------------------------------------------- */

function useScope() {
  return useSession((s) => s.currentOrgId);
}

/** GET /sites/{id}/mesh -- the mesh, live per-AP status and the create-form options.
 *  Polls every 10 s while a rollout is running so the per-AP progress moves on its own. */
export function useSiteMesh(siteId: string | null) {
  const orgId = useScope();
  return useQuery({
    queryKey: qk.sites.mesh(orgId, siteId),
    queryFn: ({ signal }) => api.get<MeshPayload>(`/sites/${siteId}/mesh`, undefined, signal),
    enabled: !!orgId && !!siteId,
    refetchInterval: (q) => (q.state.data?.mesh?.last_run?.status === "running" ? 10_000 : false),
  });
}

function useInvalidateMesh(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useScope();
  return () => void qc.invalidateQueries({ queryKey: qk.sites.mesh(orgId, siteId) });
}

/** POST /sites/{id}/mesh -- create (disabled; key generated server-side). org_admin. */
export function useCreateMesh(siteId: string | null) {
  const invalidate = useInvalidateMesh(siteId);
  return useMutation({
    mutationFn: (body: MeshCreateInput) =>
      api.post<{ mesh: SiteMesh; left_out: MeshLeftOut[] }>(`/sites/${siteId}/mesh`, body),
    onSuccess: invalidate,
  });
}

/** POST /sites/{id}/mesh/package-check/{serial} -- run the READ-ONLY capability check on one AP
 *  (over the Web CLI's RTTY path) and record the verdict. org_admin. A failure records NOTHING;
 *  its body carries `manual_fallback: true` when "Record result" should be offered instead. */
export function useRunMeshCheck(siteId: string | null) {
  const invalidate = useInvalidateMesh(siteId);
  return useMutation({
    mutationFn: (serial: string) =>
      api.post<MeshCheckResult>(`/sites/${siteId}/mesh/package-check/${serial}`, {}),
    onSuccess: invalidate,
  });
}

/** POST /sites/{id}/mesh/package-check/{serial}/record -- manual result + note (the evidence). */
export function useRecordMeshCheck(siteId: string | null) {
  const invalidate = useInvalidateMesh(siteId);
  return useMutation({
    mutationFn: (v: { serial: string; result: "pass" | "fail"; note: string }) =>
      api.post<MeshCheckResult>(`/sites/${siteId}/mesh/package-check/${v.serial}/record`, {
        result: v.result,
        note: v.note,
      }),
    onSuccess: invalidate,
  });
}

/** PATCH /sites/{id}/mesh -- edit / enable / disable / rotate key / record a package check. */
export function useUpdateMesh(siteId: string | null) {
  const invalidate = useInvalidateMesh(siteId);
  return useMutation({
    mutationFn: (body: MeshUpdateInput) => api.patch<{ mesh: SiteMesh }>(`/sites/${siteId}/mesh`, body),
    onSuccess: invalidate,
  });
}

/** DELETE /sites/{id}/mesh -- refused (409) while any AP still carries the mesh. */
export function useDeleteMesh(siteId: string | null) {
  const invalidate = useInvalidateMesh(siteId);
  return useMutation({
    mutationFn: () => api.del<{ deleted: boolean }>(`/sites/${siteId}/mesh`),
    onSuccess: invalidate,
  });
}

/** POST /sites/{id}/mesh/plan -- READ-ONLY preview (per-AP diff + order + plan_token). */
export function useMeshPlan(siteId: string | null) {
  return useMutation({
    mutationFn: () => api.post<MeshPlan>(`/sites/${siteId}/mesh/plan`),
  });
}

/** POST /sites/{id}/mesh/apply -- dry run unless dryRun is explicitly false. */
export function useApplyMesh(siteId: string | null) {
  const invalidate = useInvalidateMesh(siteId);
  return useMutation({
    mutationFn: (v: { planToken: string; dryRun: boolean }) =>
      api.post<MeshApplyResult>(`/sites/${siteId}/mesh/apply`, {
        plan_token: v.planToken,
        dry_run: v.dryRun,
      }),
    onSuccess: invalidate,
  });
}
